import type { MessageKey } from '../i18n/messages'
import { countByCategory } from './items'
import { isLocationUnlocked, LOCATION_LIST } from './locations'
import type {
  ItemStack,
  LocationExplorationState,
  LocationId,
  QuestRuntimeState,
  Survivor,
  VehicleInstance,
} from './types'
import { getVehicleDef } from './vehicles'

/** Wall-clock bucket length for stable flavor rotation */
export const HIDEOUT_FLAVOR_BUCKET_MS = 1 * 60 * 1000

/** Per-survivor food total below this enables scarcity-themed lines */
export const HIDEOUT_FLAVOR_FOOD_LOW_PER_SURVIVOR = 4

/** Per-survivor food total at or above this enables abundance-themed lines */
export const HIDEOUT_FLAVOR_FOOD_HIGH_PER_SURVIVOR = 12

export const HIDEOUT_FLAVOR_WATER_LOW_PER_SURVIVOR = 4

export const HIDEOUT_FLAVOR_WATER_HIGH_PER_SURVIVOR = 12

/** Rural South locations unlocked (reachable) before “finding my way around” copy */
export const HIDEOUT_FLAVOR_MIN_RURAL_UNLOCKED = 2

/** Minimum Gaming skill level to qualify for skill-flavored lines */
export const HIDEOUT_FLAVOR_GAMING_LEVEL_MIN = 2

export type HideoutFlavorSnapshot = {
  inventory: ItemStack[]
  survivors: Survivor[]
  progress: number
  locationExploration: Partial<Record<LocationId, LocationExplorationState>>
  quests: QuestRuntimeState
  garageVehicles: VehicleInstance[]
}

export type HideoutFlavorTag =
  | 'food_low'
  | 'food_high'
  | 'water_low'
  | 'water_high'
  | 'solo'
  | 'duo'
  | 'rural_map'
  | 'den_bosch'
  | 'bike'
  | 'car'

export type HideoutFlavorContext = {
  survivorCount: number
  food_low: boolean
  food_high: boolean
  water_low: boolean
  water_high: boolean
  solo: boolean
  duo: boolean
  rural_map: boolean
  den_bosch: boolean
  bike: boolean
  car: boolean
  gamingNames: string[]
}

export type HideoutFlavorPick = {
  key: MessageKey
  params?: Record<string, string | number>
}

function mix32(n: number): number {
  let x = Math.floor(n) >>> 0
  x ^= x >>> 16
  x = Math.imul(x, 0x7feb352d)
  x ^= x >>> 15
  x = Math.imul(x, 0x846ca68b)
  x ^= x >>> 16
  return x >>> 0
}

/** Deterministic PRNG for a single bucket */
export function mulberry32(seed: number): () => number {
  let a = mix32(seed) || 1
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), a | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function hideoutFlavorTimeBucket(nowMs: number): number {
  return Math.floor(nowMs / HIDEOUT_FLAVOR_BUCKET_MS)
}

function survivorGamingNames(survivors: Survivor[]): string[] {
  const out: string[] = []
  for (const s of survivors) {
    const g = s.skills.gaming
    if (g?.unlocked && (g.level ?? 0) >= HIDEOUT_FLAVOR_GAMING_LEVEL_MIN) {
      out.push(s.name)
    }
  }
  return out
}

export function buildHideoutFlavorContext(input: HideoutFlavorSnapshot): HideoutFlavorContext {
  const {
    inventory,
    survivors,
    progress,
    locationExploration,
    quests,
    garageVehicles,
  } = input

  const survivorCount = survivors.length
  const cap = Math.max(1, survivorCount)

  const foodTotal = countByCategory(inventory, 'food')
  const waterTotal = countByCategory(inventory, 'water')

  const foodPer = foodTotal / cap
  const waterPer = waterTotal / cap

  const scarcityOk = survivorCount > 0

  let hasBike = false
  let hasCar = false
  for (const v of garageVehicles) {
    const def = getVehicleDef(v.defId)
    if (!def) continue
    if (def.audioKind === 'bike') hasBike = true
    if (def.audioKind === 'car') hasCar = true
  }

  let ruralUnlocked = 0
  let denBoschReachable = false
  for (const loc of LOCATION_LIST) {
    if (!isLocationUnlocked(loc.id, progress, locationExploration, quests)) continue
    if (loc.region === 'rural-south') ruralUnlocked += 1
    if (loc.region === 'den-bosch') denBoschReachable = true
  }

  return {
    survivorCount,
    food_low: scarcityOk && foodPer < HIDEOUT_FLAVOR_FOOD_LOW_PER_SURVIVOR,
    food_high: scarcityOk && foodPer >= HIDEOUT_FLAVOR_FOOD_HIGH_PER_SURVIVOR,
    water_low: scarcityOk && waterPer < HIDEOUT_FLAVOR_WATER_LOW_PER_SURVIVOR,
    water_high: scarcityOk && waterPer >= HIDEOUT_FLAVOR_WATER_HIGH_PER_SURVIVOR,
    solo: survivorCount === 1,
    duo: survivorCount === 2,
    rural_map: ruralUnlocked >= HIDEOUT_FLAVOR_MIN_RURAL_UNLOCKED,
    den_bosch: denBoschReachable,
    bike: hasBike,
    car: hasCar,
    gamingNames: survivorGamingNames(survivors),
  }
}

type StaticFlavor = { kind?: 'static'; key: MessageKey; tags?: HideoutFlavorTag[] }
type PairFlavor = { kind: 'pair'; key: MessageKey; tags?: HideoutFlavorTag[] }
type GamingFlavor = { kind: 'gaming'; key: MessageKey; tags?: HideoutFlavorTag[] }
type FlavorEntry = StaticFlavor | PairFlavor | GamingFlavor

function tagsMatch(tags: HideoutFlavorTag[] | undefined, ctx: HideoutFlavorContext): boolean {
  if (!tags?.length) return true
  return tags.every((t) => ctx[t])
}

function entryEligible(entry: FlavorEntry, ctx: HideoutFlavorContext): boolean {
  if (!tagsMatch(entry.tags, ctx)) return false
  if (entry.kind === 'pair') return ctx.survivorCount >= 2
  if (entry.kind === 'gaming') return ctx.gamingNames.length >= 1
  return true
}

function pairIndices(count: number): Array<[number, number]> {
  const pairs: Array<[number, number]> = []
  for (let i = 0; i < count; i++) {
    for (let j = i + 1; j < count; j++) pairs.push([i, j])
  }
  return pairs
}

/**
 * All hideout flavor lines. Neutral entries have no tags and are always eligible.
 * Pair/gaming kinds add params at pick time.
 */
const FLAVOR_ENTRIES = [
  { key: 'hideout_flavor_neutral_01' },
  { key: 'hideout_flavor_neutral_02' },
  { key: 'hideout_flavor_neutral_03' },
  { key: 'hideout_flavor_food_low_01', tags: ['food_low'] },
  { key: 'hideout_flavor_food_low_02', tags: ['food_low'] },
  { key: 'hideout_flavor_water_low_01', tags: ['water_low'] },
  { key: 'hideout_flavor_water_low_02', tags: ['water_low'] },
  { key: 'hideout_flavor_food_high_01', tags: ['food_high'] },
  { key: 'hideout_flavor_food_high_02', tags: ['food_high'] },
  { key: 'hideout_flavor_water_high_01', tags: ['water_high'] },
  { key: 'hideout_flavor_water_high_02', tags: ['water_high'] },
  { key: 'hideout_flavor_solo_01', tags: ['solo'] },
  { key: 'hideout_flavor_solo_02', tags: ['solo'] },
  { key: 'hideout_flavor_duo_01', tags: ['duo'] },
  { key: 'hideout_flavor_duo_02', tags: ['duo'] },
  { key: 'hideout_flavor_rural_map_01', tags: ['rural_map'] },
  { key: 'hideout_flavor_rural_map_02', tags: ['rural_map'] },
  { key: 'hideout_flavor_den_bosch_01', tags: ['den_bosch'] },
  { key: 'hideout_flavor_den_bosch_02', tags: ['den_bosch'] },
  { key: 'hideout_flavor_bike_01', tags: ['bike'] },
  { key: 'hideout_flavor_bike_02', tags: ['bike'] },
  { key: 'hideout_flavor_car_01', tags: ['car'] },
  { key: 'hideout_flavor_car_02', tags: ['car'] },
  { kind: 'pair', key: 'hideout_flavor_pair_01' },
  { kind: 'pair', key: 'hideout_flavor_pair_02' },
  { kind: 'pair', key: 'hideout_flavor_pair_03' },
  { kind: 'pair', key: 'hideout_flavor_pair_04' },
  { kind: 'gaming', key: 'hideout_flavor_gaming_01' },
  { kind: 'gaming', key: 'hideout_flavor_gaming_02' },
  { kind: 'gaming', key: 'hideout_flavor_gaming_03' },
] as const satisfies readonly FlavorEntry[]

export function pickHideoutFlavorMessage(
  ctx: HideoutFlavorContext,
  survivorsOrdered: Survivor[],
  timeBucket: number,
): HideoutFlavorPick {
  const eligible = (FLAVOR_ENTRIES as readonly FlavorEntry[]).filter((e) =>
    entryEligible(e, ctx),
  )

  if (eligible.length === 0) {
    return { key: 'hideout_flavor' }
  }

  const rngPick = mulberry32(mix32(timeBucket))
  const idx = Math.floor(rngPick() * eligible.length)
  const entry = eligible[idx]!

  if (entry.kind === 'pair') {
    const rngPair = mulberry32(mix32(timeBucket + 0xa11))
    const pairs = pairIndices(survivorsOrdered.length)
    const pidx = Math.floor(rngPair() * pairs.length)
    const [ia, ib] = pairs[pidx] ?? [0, 1]
    const a = survivorsOrdered[ia]?.name ?? '?'
    const b = survivorsOrdered[ib]?.name ?? '?'
    return { key: entry.key, params: { a, b } }
  }

  if (entry.kind === 'gaming') {
    const rngG = mulberry32(mix32(timeBucket + 0xb22))
    const names = ctx.gamingNames
    const gi = Math.floor(rngG() * names.length)
    const name = names[gi] ?? '?'
    return { key: entry.key, params: { name } }
  }

  return { key: entry.key }
}
